import {
  init as sdkInit,
  miniApp,
  themeParams,
  viewport,
  backButton,
  swipeBehavior,
  closingBehavior,
} from '@tma.js/sdk';

/**
 * Порядок здесь не произвольный:
 *   1. init() поднимает мост — до него не работает ничего
 *   2. themeParams монтируется СТРОГО до miniApp, иначе часть свойств
 *      miniApp получит неверные значения
 *   3. Монтировать их одновременно через Promise.all нельзя — документация
 *      tma.js прямо предупреждает против этого
 *
 * Каждый вызов обёрнут в try — приложение обязано запускаться
 * на старых клиентах, пусть и без части возможностей.
 */
export async function initTelegram(): Promise<void> {
  // init() бросает LaunchParamsRetrieveError вне Telegram. Дальше без
  // него смысла нет, но это не повод ронять приложение — интерфейс
  // уже отрисован, а SDK-вызовы в компонентах защищены каждый сам.
  try {
    sdkInit();
  } catch (e) {
    console.warn('[SayfAzkari] SDK не инициализирован — работаем вне Telegram', e);
    return;
  }

  try {
    await themeParams.mount();
  } catch (e) {
    console.warn('[SayfAzkari] themeParams.mount не удался', e);
  }

  try {
    await miniApp.mount();
    // Сообщает Telegram, что приложение загружено и можно убирать сплэш
    miniApp.ready();
  } catch (e) {
    console.warn('[SayfAzkari] miniApp.mount не удался', e);
  }

  try {
    await viewport.mount();
    bindViewportVars();
  } catch (e) {
    console.warn('[SayfAzkari] viewport.mount не удался', e);
  }

  // Mini App открывается примерно на половину экрана — раскрываем.
  try {
    if (!viewport.isExpanded()) viewport.expand();
  } catch {
    /* старый клиент — не критично */
  }

  try {
    backButton.mount();
  } catch {
    /* нет кнопки «назад» — навигация есть и внутри интерфейса */
  }

  try {
    swipeBehavior.mount();
  } catch {
    /* Bot API < 7.7 */
  }

  try {
    closingBehavior.mount();
  } catch {
    /* не поддерживается */
  }
}

/**
 * Своя привязка вместо viewport.bindCssVars() ради одной проверки:
 * высота может прийти нулевой (Telegram ещё не сообщил размеры,
 * либо клиент ответил не сразу). Записать этот ноль в CSS — значит
 * схлопнуть .app в нулевую высоту и показать пустой экран навсегда,
 * потому что layout завязан на эту переменную.
 *
 * Ноль игнорируется, и до первого осмысленного значения работает
 * CSS-фолбэк 100dvh. Инсеты привязываются как есть: для них ноль —
 * законное значение.
 */
function bindViewportVars(): void {
  const root = document.documentElement;

  const setPx = (name: string, value: number): void => {
    root.style.setProperty(name, `${value}px`);
  };

  const applyHeights = (): void => {
    const h = viewport.height();
    const sh = viewport.stableHeight();
    if (h > 0) setPx('--tg-viewport-height', h);
    if (sh > 0) setPx('--tg-viewport-stable-height', sh);
    if (viewport.width() > 0) setPx('--tg-viewport-width', viewport.width());
  };

  const applyInsets = (): void => {
    const s = viewport.safeAreaInsets();
    const c = viewport.contentSafeAreaInsets();
    setPx('--tg-viewport-safe-area-inset-top', s.top);
    setPx('--tg-viewport-safe-area-inset-bottom', s.bottom);
    setPx('--tg-viewport-safe-area-inset-left', s.left);
    setPx('--tg-viewport-safe-area-inset-right', s.right);
    setPx('--tg-viewport-content-safe-area-inset-top', c.top);
    setPx('--tg-viewport-content-safe-area-inset-bottom', c.bottom);
    setPx('--tg-viewport-content-safe-area-inset-left', c.left);
    setPx('--tg-viewport-content-safe-area-inset-right', c.right);
  };

  applyHeights();
  applyInsets();

  viewport.height.sub(applyHeights);
  viewport.stableHeight.sub(applyHeights);
  viewport.safeAreaInsets.sub(applyInsets);
  viewport.contentSafeAreaInsets.sub(applyInsets);
}

/**
 * Telegram сворачивает Mini App свайпом вниз. У нас во весь экран
 * тап-зона счётчика, поэтому на время чтения свайп нужно глушить.
 * Bot API 7.7+; на старых клиентах просто no-op.
 */
export function setVerticalSwipes(enabled: boolean): void {
  try {
    if (!swipeBehavior.isMounted()) return;
    if (enabled) swipeBehavior.enableVertical();
    else swipeBehavior.disableVertical();
  } catch {
    /* не поддерживается — тап-счётчик всё равно работает */
  }
}

/** Подтверждение закрытия — только когда есть незавершённый прогресс */
export function setClosingConfirmation(enabled: boolean): void {
  try {
    if (!closingBehavior.isMounted()) return;
    if (enabled) closingBehavior.enableConfirmation();
    else closingBehavior.disableConfirmation();
  } catch {
    /* no-op */
  }
}
